/* eslint-disable camelcase */
import * as React from 'react';
import { useEffect, useRef } from 'react';
import type { CalendarDayPhrases, CalendarDayShape } from 'react-dates';

import cn from 'classnames';
import moment from 'moment';
import type { Moment } from 'moment';

import { DAY } from 'js/utils/DateTimeUtils';

import { useDegreeContext } from 'bundles/degree-home/components/v2/DegreeHomeContext';
import { getCalendarKey } from 'bundles/degree-home/components/v3-dpm/components/Calendar/utils/index';

import _t from 'i18n!nls/degree-home';

import { DAY_SIZE, calendarDayPhrases } from './constants/constants';
import getCalendarDaySettings from './utils/getCalendarDaySettings';

const CalendarDay: React.FunctionComponent<CalendarDayShape> = ({
  day = moment(),
  daySize = DAY_SIZE,
  isOutsideDay,
  modifiers = new Set(),
  isFocused = false,
  tabIndex = -1,
  onDayClick,
  onDayMouseEnter,
  onDayMouseLeave,
  renderDayContents,
  ariaLabelFormat = 'dddd, LL',
  phrases = calendarDayPhrases,
}) => {
  const { visibleCalendarEvents } = useDegreeContext();
  const hasEvents = visibleCalendarEvents?.[getCalendarKey(day as Moment)] || 0;
  const buttonRef = useRef<HTMLTableCellElement>(null);

  const { daySizeStyles, useDefaultCursor, selected, hoveredSpan, isOutsideRange, ariaLabel } = getCalendarDaySettings(
    day as Moment,
    ariaLabelFormat,
    daySize,
    modifiers,
    phrases as CalendarDayPhrases
  );

  const onDayClickHandler = (d: Moment, e: React.MouseEvent<HTMLTableCellElement>) => {
    if (onDayClick) onDayClick(d, e);
  };

  const onDayMouseEnterHandler = (d: Moment, e: React.MouseEvent<HTMLTableCellElement>) => {
    if (onDayMouseEnter) onDayMouseEnter(d, e);
  };

  const onDayMouseLeaveHandler = (d: Moment, e: React.MouseEvent<HTMLTableCellElement>) => {
    if (onDayMouseLeave) onDayMouseLeave(d, e);
  };

  const onKeyDownHandler = (d: Moment, e: React.KeyboardEvent<HTMLTableCellElement>) => {
    const { key } = e;
    if (key === 'Enter' || key === ' ') {
      if (onDayClick) onDayClick(d, e as unknown as React.MouseEvent<HTMLTableCellElement>);
    }
  };

  useEffect(() => {
    if (tabIndex === 0 && !isFocused) {
      buttonRef.current?.focus();
    }
  }, [isFocused, tabIndex]);

  return (
    <td
      style={daySizeStyles}
      className={cn(
        'CalendarDay CalendarDay__default',
        { CalendarDay__defaultCursor: useDefaultCursor },
        { CalendarDay__outside: isOutsideDay },
        { CalendarDay__today: modifiers?.has('today') },
        { CalendarDay__firstDayOfWeek: modifiers?.has('first-day-of-week') },
        { CalendarDay__lastDayOfWeek: modifiers?.has('last-day-of-week') },
        { CalendarDay__hovered_offset: modifiers?.has('hovered-offset') },
        { CalendarDay__hovered_start_first_possible_end: modifiers?.has('hovered-start-first-possible-end') },
        { CalendarDay__hovered_start_blocked_min_nights: modifiers?.has('hovered-start-blocked-minimum-nights') },
        { CalendarDay__highlighted_calendar: modifiers?.has('highlighted-calendar') },
        { CalendarDay__blocked_minimum_nights: modifiers?.has('blocked-minimum-nights') },
        { CalendarDay__blocked_calendar: modifiers?.has('blocked-calendar') },
        { CalendarDay__hovered_span: hoveredSpan },
        { CalendarDay__after_hovered_start: modifiers?.has('after-hovered-start') },
        { CalendarDay__selected_span: modifiers?.has('selected-span') },
        { CalendarDay__selected_start: modifiers?.has('selected-start') },
        { CalendarDay__selected_end: modifiers?.has('selected-end') },
        { CalendarDay__selected: selected && !modifiers.has('selected-span') },
        { CalendarDay__before_hovered_end: modifiers?.has('before-hovered-end') },
        { CalendarDay__no_selected_start_before_selected_end: modifiers?.has('no-selected-start-before-selected-end') },
        { CalendarDay__selected_start_in_hovered_span: modifiers?.has('selected-start-in-hovered-span') },
        { CalendarDay__selected_end_in_hovered_span: modifiers?.has('selected-end-in-hovered-span') },
        { CalendarDay__selected_start_no_selected_end: modifiers?.has('selected-start-no-selected-end') },
        { CalendarDay__selected_end_no_selected_start: modifiers?.has('selected-end-no-selected-start') },
        { CalendarDay__blocked_out_of_range: isOutsideRange }
      )}
      role="button" // eslint-disable-line jsx-a11y/no-noninteractive-element-to-interactive-role
      ref={buttonRef}
      aria-disabled={modifiers.has('blocked')}
      {...(modifiers.has('today') ? { 'aria-current': 'date' } : {})}
      aria-label={hasEvents ? [ariaLabel, _t('Has events')].join(' ') : ariaLabel}
      onMouseEnter={(e) => {
        onDayMouseEnterHandler(day as Moment, e);
      }}
      onMouseLeave={(e) => {
        onDayMouseLeaveHandler(day as Moment, e);
      }}
      onMouseUp={(e) => {
        e.currentTarget.blur();
      }}
      onClick={(e) => {
        onDayClickHandler(day as Moment, e);
      }}
      onKeyDown={(e) => {
        onKeyDownHandler(day as Moment, e);
      }}
      tabIndex={tabIndex}
    >
      {renderDayContents ? renderDayContents(day as Moment, modifiers) : day?.format(DAY)}
    </td>
  );
};

export default CalendarDay;
